import React, { type FC, useMemo, useState } from "react";
import { Flex, Text, Box, SimpleGrid, VStack, AspectRatio } from "@chakra-ui/react";
import { type GetStaticProps, type NextPage } from "next";
import Image from "next/image";
import { getClient, groq, type Page as SanityPage, getPage, getAnnouncementBanner, type Banner } from "lib/sanity";
import { Section } from "components/section";
import { Page } from "components/page";
import { SidebarButton } from "components/sidebar";
import { ResourceCard } from "components/resource-card";
import { FeaturedResourceCard } from "components/featured-resource-card";
import { DemoForm } from "components/demo-form";
import { useHeaderHeight } from "contexts/header-height";
import { getPageMetaImage } from "utils";
import { Rule } from "ui/rule";
import { redirectedBlogs } from "../../constants/redirected-blogs";
import { blogBlurDataUrl } from "../../constants/blur-data-urls";

type PageProps = {
  page: SanityPage;
  categories: Category[];
  posts: Post[];
  announcementBanner?: Banner;
};

const Blog: NextPage<Readonly<PageProps>> = ({ page, categories, posts: allPosts, announcementBanner }) => {
  const [selectedCategory, setSelectedCategory] = useState<Category | undefined>();
  const { headerHeight } = useHeaderHeight();

  const featuredPost = allPosts[0];

  const posts = useMemo(() => {
    if (!selectedCategory) {
      return allPosts;
    }

    return allPosts.filter((post) => {
      return post.category.name === selectedCategory.name;
    });
  }, [allPosts, selectedCategory]);

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <Section center my={{ base: 8, md: 16 }}>
        <Flex gap={6}>
          <Flex
            as="aside"
            display={{ base: "none", lg: "flex" }}
            top={`${headerHeight + 16 * 4}px`}
            flex="0 0 240px"
            direction="column"
            pos="sticky"
            w="240px"
            h="100%"
            // MaxHeight = headerHeight + 20px top padding + 20px bottom padding for breathing room
            maxHeight={`calc(100vh - ${headerHeight + 40}px)`}
            // Add right padding for scroll bar
            pr={6}
            overflowY="auto"
          >
            <Text fontWeight="semibold" fontSize="lg">
              See more stories
            </Text>

            <VStack mt={2} spacing={2} align="left">
              <SidebarButton
                isActive={selectedCategory === undefined}
                onClick={() => {
                  setSelectedCategory(undefined);
                }}
              >
                All
              </SidebarButton>

              {categories.map((category) => (
                <SidebarButton
                  key={category.slug}
                  isActive={category === selectedCategory}
                  onClick={() => {
                    setSelectedCategory(category);
                  }}
                >
                  {category.name}
                </SidebarButton>
              ))}
            </VStack>

            <Rule />

            <Text mb={4} fontWeight="semibold" fontSize="lg">
              Book a demo
            </Text>

            <DemoForm />
          </Flex>

          <Box flex={1}>
            <FeaturedBlogCard {...featuredPost} />

            <SimpleGrid mt={{ base: 8, md: 16 }} columns={{ base: 1, md: 2, lg: 3 }} gap={8}>
              {posts.slice(1).map((post, index) => (
                <BlogCard key={post.slug} priority={index < 3} {...post} />
              ))}
            </SimpleGrid>
          </Box>
        </Flex>
      </Section>
    </Page>
  );
};

type Category = {
  name: string;
  slug: string;
};

const fetchCategories = async (): Promise<Category[]> => {
  const categories: Category[] = await getClient().fetch(groq`
    *[_type == "blogCategory"] {
      name,
      slug
    }
  `);

  return categories.filter((category) => category.slug !== "external");
};

export type Post = {
  imageUrl: string;
  title: string;
  excerpt: string;
  slug: string;
  authors: Array<{
    name: string;
  }>;
  category: {
    name: string;
  };
  publishedAt: string;
};

const fetchPosts = async (): Promise<Post[]> => {
  return getClient().fetch(groq`
    *[_type == "blog" && !hidden && !(slug in [${redirectedBlogs.map((slug) => "'" + slug + "'")}]) ] {
      title,
      slug,
      excerpt,
      'imageUrl': image.asset->url,
      imageAlt,
      category->{name},
      publishedAt,
      authors[]->{name}
    } | order(publishedAt desc)
  `);
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [categories, posts, page, announcementBanner] = await Promise.all([
    fetchCategories(),
    fetchPosts(),
    getPage("/blog"),
    getAnnouncementBanner(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      categories,
      page,
      posts,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export default Blog;

const BlogCard: FC<Post & { priority?: boolean }> = ({ title, slug, excerpt, imageUrl, publishedAt, priority }) => (
  <ResourceCard
    href={`/blog/${slug}`}
    linkAriaLabel={`Read ${title}.`}
    title={title}
    excerpt={excerpt}
    actionLabel="Read"
    image={
      <AspectRatio ratio={2 / 1} pos="relative" width="100%" display={["none", "block"]}>
        <Image
          fill
          sizes="100%"
          src={`${imageUrl}?w=800`}
          alt={title + "."}
          placeholder="blur"
          blurDataURL={blogBlurDataUrl}
          priority={priority}
          style={{
            objectFit: "cover",
          }}
        />
      </AspectRatio>
    }
    publishedAt={publishedAt}
  />
);

const FeaturedBlogCard: FC<Post> = ({ title, slug, excerpt, imageUrl, publishedAt }) => (
  <FeaturedResourceCard
    title={title}
    href={`/blog/${slug}`}
    linkAriaLabel={`Read ${title}.`}
    excerpt={excerpt}
    actionLabel="Read"
    image={
      <AspectRatio ratio={2 / 1} pos="relative" width="100%" display={["none", "block"]}>
        <Image
          priority
          fill
          sizes="100%"
          src={`${imageUrl}?w=1200`}
          alt={title + "."}
          placeholder="blur"
          blurDataURL={blogBlurDataUrl}
        />
      </AspectRatio>
    }
    publishedAt={publishedAt}
  />
);
